import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Type, AlignLeft, List, Heading, Image as ImageIcon, FileText, Save, ClipboardList } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const TYPES = [
  { t: "text", l: "Short text", icon: Type },
  { t: "textarea", l: "Paragraph", icon: AlignLeft },
  { t: "dropdown", l: "Dropdown", icon: List },
  { t: "header", l: "Label", icon: Heading },
  { t: "image", l: "Image upload", icon: ImageIcon },
  { t: "file", l: "File upload", icon: FileText },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((x) => [x.t, x.l]));
const uid = () => `fld_${Math.random().toString(36).slice(2, 10)}`;
const inputCls = "rounded-lg bg-surface glow-border px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";

export default function CustomFormBuilder({ initial = [], onSave }) {
  const [fields, setFields] = useState(() => (initial || []).map((f) => ({ ...f })));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const mutate = (fn) => { setFields((prev) => fn(prev.map((x) => ({ ...x })))); setDirty(true); };
  const add = (t) => mutate((p) => [...p, {
    id: uid(), type: t, label: t === "header" ? "Section title" : "New question", required: false,
    ...(t === "dropdown" ? { options: ["Option 1", "Option 2"] } : {}),
  }]);
  const upd = (i, patch) => mutate((p) => { p[i] = { ...p[i], ...patch }; return p; });
  const del = (i) => mutate((p) => { p.splice(i, 1); return p; });
  const move = (i, d) => mutate((p) => { const j = i + d; if (j < 0 || j >= p.length) return p; [p[i], p[j]] = [p[j], p[i]]; return p; });

  const save = async () => {
    setSaving(true);
    try { await onSave(fields); setDirty(false); toast.success("Registration form saved"); }
    catch (e) { toast.error("Could not save form"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="custom-form-builder">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h3 className="font-display font-bold flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary-c" /> Registration form builder</h3>
        <button data-testid="save-custom-form" onClick={save} disabled={saving || !dirty}
          className="btn-glow rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : dirty ? "Save form" : "Saved"}
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--text-2)" }}>Add custom questions, labels and upload fields to your public sign-up page. Players fill these in when they register.</p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="field-toolbar">
        {TYPES.map(({ t, l, icon: Icon }) => (
          <button key={t} data-testid={`add-field-${t}`} onClick={() => add(t)}
            className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 hover:scale-105 transition-transform" style={{ color: "var(--text)" }}>
            <Icon className="w-3.5 h-3.5 text-primary-c" /> {l}
          </button>
        ))}
      </div>

      {fields.length === 0 ? (
        <p className="text-sm text-center py-6 rounded-xl" style={{ background: "var(--bg)", color: "var(--text-2)" }} data-testid="builder-empty">
          No custom fields yet. Players will only see the standard category sign-up. Add a field above to get started.
        </p>
      ) : (
        <div className="space-y-3" data-testid="builder-fields">
          {fields.map((f, i) => (
            <div key={f.id} data-testid={`builder-field-${f.id}`} className="rounded-xl p-3" style={{ background: "var(--bg)" }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] mono uppercase tracking-wider glass rounded-full px-2 py-0.5" style={{ color: "var(--primary-ink)" }}>{TYPE_LABEL[f.type]}</span>
                <div className="ml-auto flex items-center gap-1">
                  <button data-testid={`field-up-${f.id}`} onClick={() => move(i, -1)} className="w-6 h-6 rounded glass flex items-center justify-center"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button data-testid={`field-down-${f.id}`} onClick={() => move(i, 1)} className="w-6 h-6 rounded glass flex items-center justify-center"><ChevronDown className="w-3.5 h-3.5" /></button>
                  <button data-testid={`field-remove-${f.id}`} onClick={() => del(i)} className="w-6 h-6 rounded glass flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
                </div>
              </div>
              <input data-testid={`field-label-${f.id}`} value={f.label} onChange={(e) => upd(i, { label: e.target.value })}
                placeholder={f.type === "header" ? "Section title / instructions" : "Question label"} className={`${inputCls} w-full`} style={{ color: "var(--text)" }} />
              {f.type === "dropdown" && (
                <textarea data-testid={`field-options-${f.id}`} value={(f.options || []).join("\n")} onChange={(e) => upd(i, { options: e.target.value.split("\n") })}
                  rows={3} placeholder="One option per line" className={`${inputCls} w-full mt-2`} style={{ color: "var(--text)" }} />
              )}
              {f.type !== "header" && (
                <label className="flex items-center gap-2 text-xs mt-2" style={{ color: "var(--text-2)" }}>
                  <Switch data-testid={`field-required-${f.id}`} checked={!!f.required} onCheckedChange={(v) => upd(i, { required: v })} /> Required field
                </label>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
